import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Download, FileText, Loader2, X, Check, ShieldCheck } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PdfDownloadGate = ({ post, variant = "card" }) => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", company: "", country: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  // ESC key + body lock
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email || !form.company || !form.country) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/blog/lead/${post.slug}`, form, {
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Spiruva-${post.slug}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 500);
      setDone(true);
      toast.success("Your branded PDF is downloading.");
    } catch (err) {
      console.error(err);
      toast.error("Could not generate PDF. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const trigger =
    variant === "inline" ? (
      <button
        onClick={() => setOpen(true)}
        data-testid="pdf-gate-inline-trigger"
        className="btn-primary"
      >
        <Download size={14} /> Download Full PDF Report
      </button>
    ) : (
      <div className="bg-brand-cream border border-brand-gold/30 p-6 relative overflow-hidden" data-testid="pdf-gate-card">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 bg-brand-gold/15 text-[#8a6e1f] border border-brand-gold/40 flex items-center justify-center shrink-0">
            <FileText size={16} />
          </div>
          <div>
            <p className="eyebrow text-[#8a6e1f] mb-1">◦  Premium Download</p>
            <h3 className="font-display text-xl text-brand-black leading-tight">Get the typeset PDF version.</h3>
            <p className="text-xs text-brand-black/65 mt-2 leading-relaxed">Beautifully branded, sharable with your procurement team.</p>
          </div>
        </div>
        <button
          onClick={() => setOpen(true)}
          data-testid="pdf-gate-card-trigger"
          className="mt-5 w-full inline-flex items-center justify-center gap-2 bg-brand-gold text-brand-black py-3 text-xs uppercase tracking-[0.18em] font-medium hover:bg-brand-black hover:text-brand-cream transition"
        >
          <Download size={13} /> Download PDF
        </button>
      </div>
    );

  return (
    <>
      {trigger}

      {open && (
        <div
          className="fixed inset-0 bg-brand-black/70 z-[70] flex items-center justify-center p-4 md:p-6 animate-fade-in"
          data-testid="pdf-gate-modal"
          role="dialog"
          aria-modal="true"
          onClick={(e) => { if (e.target === e.currentTarget) setOpen(false); }}
        >
          <div className="bg-white max-w-lg w-full p-8 md:p-10 border border-brand-green/15 relative max-h-[90vh] overflow-y-auto">
            <button
              onClick={() => setOpen(false)}
              aria-label="Close"
              data-testid="pdf-gate-close"
              className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center text-brand-black/60 hover:text-brand-black"
            >
              <X size={18} />
            </button>

            {!done ? (
              <>
                <div className="w-12 h-12 bg-brand-gold/15 text-[#8a6e1f] border border-brand-gold/40 flex items-center justify-center mb-5">
                  <FileText size={18} />
                </div>
                <p className="eyebrow mb-2">◦  Premium PDF Report</p>
                <h2 className="font-display text-3xl text-brand-black leading-tight">{post.title.length > 90 ? post.title.slice(0, 90) + "…" : post.title}</h2>
                <p className="text-sm text-brand-black/65 mt-3 leading-relaxed">
                  Get the branded, typeset PDF version. Shareable with your sourcing, R&D, and procurement teams. We use your details only to follow up with relevant market intelligence.
                </p>

                <form onSubmit={submit} className="mt-7 space-y-3" data-testid="pdf-gate-form">
                  <Field label="Full Name" value={form.name} onChange={(v) => setField("name", v)} testid="pdf-gate-name" />
                  <Field label="Work Email *" required type="email" value={form.email} onChange={(v) => setField("email", v)} testid="pdf-gate-email" />
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <Field label="Company *" required value={form.company} onChange={(v) => setField("company", v)} testid="pdf-gate-company" />
                    <Field label="Country *" required value={form.country} onChange={(v) => setField("country", v)} testid="pdf-gate-country" />
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="pdf-gate-submit"
                    className="w-full btn-primary disabled:opacity-60 mt-5"
                  >
                    {loading ? <><Loader2 size={14} className="animate-spin" /> Generating PDF</> : <><Download size={14} /> Unlock & Download PDF</>}
                  </button>

                  <p className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest text-brand-black/45 pt-2">
                    <ShieldCheck size={11} /> Your details are confidential · No spam
                  </p>
                </form>
              </>
            ) : (
              <div className="text-center py-4">
                <div className="inline-flex items-center justify-center w-14 h-14 bg-brand-green text-brand-cream mb-5">
                  <Check size={22} />
                </div>
                <h2 className="font-display text-3xl text-brand-black">Your PDF is downloading.</h2>
                <p className="text-sm text-brand-black/65 mt-3 leading-relaxed">If the download didn't start, check your browser's download bar or click the button again.</p>
                <button
                  onClick={() => setDone(false)}
                  className="btn-secondary mt-7"
                >
                  Download Again
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

const Field = ({ label, value, onChange, type = "text", required = false, testid }) => (
  <div>
    <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-brand-black/55">{label}</span>
    <input
      type={type}
      required={required}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      data-testid={testid}
      className="w-full mt-1 px-4 py-2.5 bg-brand-cream/40 border border-brand-green/20 focus:border-brand-teal focus:outline-none text-sm font-body"
    />
  </div>
);

export default PdfDownloadGate;
